import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const itemStoreBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['item-store']),
});

export const itemIdentifierSchema = z.object({
  id: z.number().int(),
});

// Using loose to allow additional properties for item entities for events
// that carry full or partial item data. Replace it with item Zod Schema when thats ready
const itemEntitySchema = itemIdentifierSchema
  .extend({
    doneDateTime: z.string().nullish(),
  })
  .loose();

export const itemDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: itemEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: itemEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    change: itemEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: itemIdentifierSchema,
  }),
]);

export const integrationStatusSchema = z.enum([
  'CREATED',
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'DELETING',
]);

export const synchronizationStatusSchema = z.enum(['ENABLED', 'DISABLED', 'DELETING']);

export const integrationIdentifierSchema = z.object({
  id: z.number().int(),
});

export const integrationEntitySchema = z
  .object({
    id: z.number().int(),
    status: integrationStatusSchema,
    synchronizationStatus: synchronizationStatusSchema.optional(),
    lastPullSyncAt: z.string().nullish(),
    lastPushSyncAt: z.string().nullish(),
  })
  .loose();

export const integrationDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: integrationEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: integrationEntitySchema,
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: integrationIdentifierSchema,
  }),
]);

export const dependencyDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('dependency'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const estimationDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const estimationSchemeDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('estimation-scheme'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemStatusDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-status'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemTypeDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemTypeLevelDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-type-level'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const scheduleDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('schedule'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const fieldDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const fieldValueDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('field-value'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const picklistOptionDataChangeDTOSchema = discriminatedUnion('eventType', [
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  itemStoreBaseCdcEventSchema.extend({
    entityType: z.literal('picklist-option'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

export const itemStoreDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(
    discriminatedUnion('entityType', [
      itemDataChangeDTOSchema,
      integrationDataChangeDTOSchema,
      dependencyDataChangeDTOSchema,
      estimationDataChangeDTOSchema,
      estimationSchemeDataChangeDTOSchema,
      itemStatusDataChangeDTOSchema,
      itemTypeDataChangeDTOSchema,
      itemTypeLevelDataChangeDTOSchema,
      scheduleDataChangeDTOSchema,
      fieldDataChangeDTOSchema,
      fieldValueDataChangeDTOSchema,
      picklistOptionDataChangeDTOSchema,
    ]),
  ),
  authContext: cdcAuthContextSchema,
});

export type ItemStoreDataChangeDTO = z.output<typeof itemStoreDataChangeDTOSchema>;

export type ItemDataChangeDTO = Extract<ItemStoreDataChangeDTO['data'], { entityType: 'item' }>;
export type ItemDeleteDataChangeDTO = Extract<ItemDataChangeDTO, { eventType: 'deleted' }>;
